import './layout.scss';

import { Suspense, useCallback, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';

import useAppNavigate from '@/shared/hooks/useAppNavigation';
import { ROUTES } from '@/shared/routes';
import { MainLoader } from '@/shared/ui';
import { nativeLog } from '@/shared/webview/base-actions';
import Oops from '@/shared/widgets/errors/oops';
import { useAppSelector } from '@/store';

import { BottomTabBar, Header, Sidebar } from './widgets';

const Layout: React.FC = () => {
  const { t } = useTranslation();
  const { navigate } = useAppNavigate();
  const { pathname } = useLocation();

  const { isInWebView } = useAppSelector((state) => state.app);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobileMenuActive = pathname.includes(ROUTES.nativeMenu);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const openMobileMenu = useCallback(() => {
    if (isInWebView) navigate(ROUTES.nativeMenu);
    else setIsMobileMenuOpen(true);
  }, [isInWebView]);

  return (
    <div className="layout">
      {!isInWebView && (
        <Sidebar
          isMobileMenuOpen={isMobileMenuOpen}
          onMobileMenuClose={closeMobileMenu}
        />
      )}

      <div className="layout__content">
        <Header />

        <main className="layout__main">
          <ErrorBoundary
            onError={(error) =>
              isInWebView
                ? nativeLog('layout error', error)
                : console.error(error)
            }
            fallback={
              <div className="layout__error">
                <Oops
                  redirect={{
                    text: t('errors.redirectHome'),
                    href: ROUTES.leasing
                  }}
                />
              </div>
            }
          >
            <Suspense fallback={<MainLoader fullScreen />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>

        <BottomTabBar
          isMobileMenuOpen={isInWebView ? isMobileMenuActive : isMobileMenuOpen}
          onMobileMenuToggle={openMobileMenu}
        />
      </div>
    </div>
  );
};

export default Layout;
